import { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlay, FiMapPin } = FiIcons;

// YouTube testimonial videos (shown first)
const youtubeVideos = [
  { id: 'yt1', embedId: 'vRzrNK9XPmQ', title: 'Want More Customers? See How Manivanan Did It with FunnelsDone', name: 'Dr. Valarmathi', type: 'youtube' },
  { id: 'yt2', embedId: 'v3EEtDIaC3w', title: 'How Funnels Done Transformed My Coaching Business', name: 'Woman Entrepreneur Coach', type: 'youtube' },
  { id: 'yt3', embedId: 'IrfPwkcSTB0', title: 'From Struggling to 2 Lakhs: My Funnels Done Success Story', name: 'Bridal Lead Generation Coach', type: 'youtube' },
  { id: 'yt4', embedId: 'F26JiMS-w4I', title: 'Overcoming Coaching Business Challenges with Funnels Done', name: 'Coaching Business', type: 'youtube' },
  { id: 'yt5', embedId: 'yWzJt7jv5CM', title: 'How I Made 30 Lakhs in a Year as a Coach without Quitting my Job', name: 'Professor', type: 'youtube' },
  { id: 'yt6', embedId: 'L0jJ2NHIQ8w', title: 'The Secret Behind Mariyappan Business Growth | FunnelsDone AI', name: 'Iniya Jewelers Madurai', type: 'youtube' },
];

// Short vertical videos (shown after YouTube videos)
const shorts = [
  {
    id: 'short1',
    videoUrl: 'https://assets.cdn.filesafe.space/Te8QGpu12zbMxZIVQlZi/media/6999718e3a2afd0d9985e1ae.mp4',
    name: 'Esakkiammal',
    location: 'Tamil Nadu',
    impact: 'Gained the tools and mindset to scale her local business globally, setting massive revenue goals up to ₹1 crore using FunnelsDone.',
    type: 'short'
  },
  {
    id: 'short2',
    videoUrl: 'https://assets.cdn.filesafe.space/Te8QGpu12zbMxZIVQlZi/media/6999718ed83aeca78e98b1f3.mp4',
    name: 'Dhananjayan',
    location: 'Chennai',
    impact: 'Learned the exact digital framework needed to effectively showcase his niche healing and Vastu services to a worldwide audience.',
    type: 'short'
  },
  {
    id: 'short3',
    videoUrl: 'https://assets.cdn.filesafe.space/Te8QGpu12zbMxZIVQlZi/media/6999718e20c035f6ae861689.mp4',
    name: 'Vijayaraj',
    location: 'Ambur',
    impact: 'Motivated to achieve self-reliance by starting an academic e-book, proving age is no barrier to digital growth.',
    type: 'short'
  },
  {
    id: 'short4',
    videoUrl: 'https://assets.cdn.filesafe.space/Te8QGpu12zbMxZIVQlZi/media/6999718ed83aec504298b1f2.mp4',
    name: 'Gunaseelan',
    location: 'Vellore',
    impact: 'Discovered 50+ growth-hacking techniques that bridged the gap between his wellness expertise and reaching paying customers online.',
    type: 'short'
  },
];

function YouTubeCard({ v }) {
  const [playing, setPlaying] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      className="flex flex-col rounded-2xl overflow-hidden border-2 border-slate-700 hover:border-blue-500 transition-all duration-300 bg-slate-800/50 hover:scale-105 transform"
    >
      <div className="relative bg-slate-900" style={{ aspectRatio: '16/9' }}>
        {playing ? (
          <iframe
            src={`https://www.youtube.com/embed/${v.embedId}?autoplay=1&rel=0`}
            title={v.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full border-0"
          />
        ) : (
          <>
            <img
              src={`https://img.youtube.com/vi/${v.embedId}/maxresdefault.jpg`}
              alt={v.title}
              className="w-full h-full object-cover opacity-60"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <button
                onClick={() => setPlaying(true)}
                className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center hover:scale-110 transition-transform shadow-xl shadow-blue-500/40 hover:from-blue-400 hover:to-blue-500"
              >
                <SafeIcon icon={FiPlay} className="text-white ml-1 text-2xl" />
              </button>
            </div>
          </>
        )}
      </div>

      <div className="p-4 flex flex-col flex-grow justify-start">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold shrink-0">
            {v.name[0]}
          </div>
          <div>
            <p className="font-bold text-sm text-white">{v.name}</p>
          </div>
        </div>
        <p className="text-xs leading-relaxed text-gray-300">{v.title}</p>
      </div>
    </motion.div>
  );
}

function ShortCard({ s }) {
  const [playing, setPlaying] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      className="flex flex-col rounded-2xl overflow-hidden border-2 border-slate-700 hover:border-blue-500 transition-all duration-300 bg-slate-800/50 hover:scale-105 transform"
    >
      <div className="relative bg-slate-900" style={{ aspectRatio: '9/16', maxHeight: 400 }}>
        {playing ? (
          <video
            src={s.videoUrl}
            autoPlay
            controls
            playsInline
            className="w-full h-full object-cover"
          />
        ) : (
          <>
            <video
              src={s.videoUrl + '#t=0.5'}
              className="w-full h-full object-cover opacity-50"
              preload="metadata"
              playsInline
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <button
                onClick={() => setPlaying(true)}
                className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center hover:scale-110 transition-transform shadow-xl shadow-blue-500/40 hover:from-blue-400 hover:to-blue-500"
              >
                <SafeIcon icon={FiPlay} className="text-white ml-1 text-2xl" />
              </button>
            </div>
          </>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold">
            {s.name[0]}
          </div>
          <div>
            <p className="font-bold text-sm text-white">{s.name}</p>
            <div className="flex items-center gap-1 text-gray-400">
              <SafeIcon icon={FiMapPin} className="text-xs" />
              <span className="text-xs">{s.location}</span>
            </div>
          </div>
        </div>
        <p className="text-xs leading-relaxed text-gray-300">{s.impact}</p>
      </div>
    </motion.div>
  );
}

export default function VideoTestimonials() {
  return (
    <section className="py-20 px-4 bg-slate-800/50">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-blue-400 text-sm font-semibold uppercase tracking-widest">Student Wins</span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mt-4 mb-4">
            Real Stories, <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">Real Results</span>
          </h2>
          <p className="text-xl text-gray-300">Watch how our members transformed their businesses</p>
        </motion.div>

        {/* YouTube Videos Section */}
        <div className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h3 className="text-2xl font-bold text-blue-400 mb-2">Success Stories</h3>
            <p className="text-gray-400">Hear from our students — no actors, no scripts</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {youtubeVideos.map((v) => (
              <YouTubeCard key={v.id} v={v} />
            ))}
          </div>
        </div>

        {/* Short Videos Section */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h3 className="text-2xl font-bold text-blue-400 mb-2">Quick Wins</h3>
            <p className="text-gray-400">Short stories of transformation</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {shorts.map((s) => (
              <ShortCard key={s.id} s={s} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}